import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

// Renders a compact Chart.js chart inside an objective dashboard tile.
// Uses the generic aggregate data shape from ObjectiveAggregator.
// Works with any measure_model -- no hardcoded data source branching.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    aggregateData: Object,
    chartConfig: { type: Object, default: {} },
    measureModel: { type: String, default: "ResponseResult" }
  }

  connect() {
    if (this.hasCanvasTarget && this.hasAggregateDataValue) {
      this.renderChart()
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    const config = this.resolvedConfig()
    const data = this.aggregateDataValue
    const grouped = data.grouped_data || {}

    // Try the primary grouping first (by_response_option is the generic key),
    // then fall back to by_event_value for stock events
    const primaryData = grouped.by_response_option || grouped.by_event_value || {}

    // For stock events, humanise the C/I/O/X labels
    const eventLabels = { C: "Cut In", I: "In Stock", O: "Out of Stock", X: "Not Ranged" }
    const isStockEvent = this.measureModelValue === "StockEvent"

    const rawLabels = Object.keys(primaryData)
    const labels = isStockEvent ? rawLabels.map(k => eventLabels[k] || k) : rawLabels
    const values = Object.values(primaryData)

    if (labels.length === 0) {
      this.renderEmptyState()
      return
    }

    if (config.chart_kind === "text") {
      this.renderTextDisplay(values, config)
      return
    }

    const chartType = (config.chart_kind === "bar" || config.chart_kind === "column") ? "bar" : "doughnut"
    this.renderChartInstance(chartType, labels, values, config)
  }

  renderChartInstance(type, labels, values, config) {
    const ctx = this.canvasTarget.getContext("2d")
    const colors = this.buildColors(labels.length, config)

    this.chart = new Chart(ctx, {
      type: type,
      data: {
        labels: labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: type === "doughnut" ? 2 : 1,
          borderColor: type === "doughnut" ? "#ffffff" : colors
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || ""
                const value = context.parsed.y ?? context.parsed ?? 0
                return `${label}: ${value}`
              }
            }
          }
        },
        ...(type === "bar" ? {
          scales: {
            y: { beginAtZero: true, display: false },
            x: { display: false }
          }
        } : {})
      }
    })
  }

  renderTextDisplay(values, config) {
    // Replace the canvas with a big number display
    const container = this.canvasTarget.parentElement
    const total = values.reduce((a, b) => a + b, 0)
    container.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; flex-direction: column;">
        <div style="font-size: 3rem; font-weight: 700; color: ${config.primary_color}; line-height: 1;">${total.toLocaleString()}</div>
        <div style="font-size: 0.75rem; color: #9ca3af; margin-top: 4px;">${this.measureModelValue.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}</div>
      </div>
    `
  }

  renderEmptyState() {
    const ctx = this.canvasTarget.getContext("2d")
    ctx.fillStyle = "#d1d5db"
    ctx.font = "11px system-ui"
    ctx.textAlign = "center"
    ctx.fillText("No data", this.canvasTarget.width / 2, this.canvasTarget.height / 2)
  }

  resolvedConfig() {
    const defaults = { chart_kind: "doughnut", primary_color: "#003d68", secondary_color: "#e5e7eb", label_style: "percentage" }
    return Object.assign({}, defaults, this.chartConfigValue)
  }

  buildColors(count, config) {
    const palette = [
      config.primary_color,
      "rgba(16, 185, 129, 0.8)",
      "rgba(251, 146, 60, 0.8)",
      "rgba(239, 68, 68, 0.8)",
      "rgba(168, 85, 247, 0.8)",
      "rgba(236, 72, 153, 0.8)"
    ]
    if (count <= 2) {
      return [config.primary_color, config.secondary_color].slice(0, count)
    }
    return palette.slice(0, Math.max(count, palette.length))
  }
}
